import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, useRef } from "react";
import { db } from "../data/base";
import { doc, getDoc } from "firebase/firestore";
import { Select, Typography, Button, Row, Col, Input } from "antd";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";

import { modelGlassesActions } from "../redux/brandViewer/modelGlasses.js";
import { brandViewerActions } from "../redux/brandViewer/brandViewer";
import ModelContainerFrontEnd from "../components/ModelViewer/ModelContainerFrontEnd.js";
import ThreeJSViewer from "../components/ModelViewer/ThreeJSViewer/ThreeJSViewer.js";
import { useModelLoader } from "../components/ModelViewer/ThreeJSViewer/ThreeJSViewer.js";
import { storeMaterialPreset, fetchAllMaterialPresets, fetchModelMaterialPresets, updateModelMaterialPresets } from "../services/api/materialPresetsApi.js";

const { Option } = Select;
const { Text } = Typography

const MaterialPresets = () => {
    const params = useParams();
    const dispatch = useDispatch();

    const catalogVariantId = params.TokenCatalogVariant;
    const variantId = useRef('');

    // Stato per gestire gli errori
    const [error, setError] = useState(null);

    const viewerRef = useRef();
    const modelCut = useRef(null);

    // Ref per i campi di input del form di creazione preset
    const presetNameRef = useRef();
    const newMaterialNameRef = useRef();

    // Stato per il nome del materiale selezionato
    const [selectedMaterialName, setSelectedMaterialName] = useState(undefined);

    // Stati per l'applicazione/rimozione dei preset
    const [selectedMaterial, setSelectedMaterial] = useState(undefined);
    const [selectedPreset, setSelectedPreset] = useState(undefined);

    // Seleziona il modello in view
    const modelInView = useSelector((state) =>
        state.modelGlasses.find((modelState) => modelState.id === variantId.current)
    );

    // Seleziona la lista dei nomi dei materiali e le loro configurazioni
    const { materialNamesList, materialConfigsState } = useSelector((state) => state.model);

    // Stati aggiuntivi
    const [src, setSrc] = useState("");
    const [srcCutVers, setSrcCutVers] = useState("");
    const [usdzSrc, setUsdzSrc] = useState(null);
    const [materialPresets, setMaterialPresets] = useState([]);
    const [modelMaterialPresets, setModelMaterialPresets] = useState(new Map());

    const PROJECT_PARAMS = {
        ar: "true",
        autoRotate: false,
        backgroundColor: "#ffffff",
        isBackgroundImage: false,
        backgroundImage: "",
        cameraControls: true,
        cameraOrbit: "",
        disableZoom: false,
        exposure: 1,
        lightExposition: 1,
        shadowIntensity: 0.5,
        shadowSmooth: 0.3,
    };

    // useEffect: carica gli eventuali preset applicati alla variante
    useEffect(() => {
        async function fetchVariantData() {
            try {
                // Recupero dell'id della variante principale da CataloguesVariants
                const catalogVariantDoc = await getDoc(doc(db, "CataloguesVariants", catalogVariantId));
                if (catalogVariantDoc && catalogVariantDoc.exists() && catalogVariantDoc.data()) {
                    const catalogVariantData = catalogVariantDoc.data();
                    variantId.current = catalogVariantData.mainVariantRef;
                } else {
                    variantId.current = catalogVariantId;
                }
                // Recupero dei dati della variante principale da Variants
                const variantDoc = await getDoc(doc(db, "Variants", variantId.current));
                const data = variantDoc.data();
                data.lastUpdate = data.lastUpdate.toDate().toISOString();
                dispatch(modelGlassesActions.setModel({ data, id: variantId.current }));
                setSrc(data.urlGlobalComplete);
                setSrcCutVers(data.urlGlobal);
                setUsdzSrc(data.urlUsdzComplete);
                dispatch(brandViewerActions.setSelectedProject(PROJECT_PARAMS));
                if (data.list_material_presets) {
                    // fetch dei preset già applicati al modello.
                    fetchModelMaterialPresets(data)
                        .then((presets) => {
                            setModelMaterialPresets(presets);
                        })
                        .catch((error) => {
                            console.error("Error during model material presets loading: ", error);
                        });
                }
            } catch (error) {
                console.error("Error during model data loading:", error);
                setError("Error during model data loading: " + error.message);
            }
        }
        fetchVariantData();
    }, [catalogVariantId, dispatch]);

    // useEffect: carica tutti i preset disponibili su firebase.
    useEffect(() => {
        fetchMaterialPresets();
    }, []);

    // useEffect: imposta il preset selezionato se il material selezionato ha un preset applicato.
    useEffect(() => {
        if (selectedMaterial && modelMaterialPresets.has(selectedMaterial)) {
            setSelectedPreset(modelMaterialPresets.get(selectedMaterial).id);
        }
    }, [selectedMaterial, modelMaterialPresets]);

    const { modelUrl, isLoaded, error: loadingError } = useModelLoader(srcCutVers);

    // useEffect: carica versione modello 3D con aste tagliate.
    useEffect(() => {
        if (loadingError || (!isLoaded || !modelUrl)) return;

        const loader = new GLTFLoader();
        loader.loadAsync(modelUrl)
            .then((gltf) => {
                modelCut.current = gltf.scene;
                modelCut.current.animations = gltf.animations;
            })
            .catch((error) => {
                console.error("Error during model cut version loading:", error);
            })
    }, [srcCutVers, modelUrl, isLoaded]);

    const fetchMaterialPresets = async () => {
        try {
            const presets = await fetchAllMaterialPresets();
            setMaterialPresets(presets);
        } catch (error) {
            console.error("Error during material presets loading:", error);
            setError(error.message);
        }
    }

    const handleMaterialChange = (value) => {
        setSelectedMaterial(value);
    };

    const handleMaterialNameChange = (value) => {
        setSelectedMaterialName(value);
    };

    const handlePresetChange = (value) => {
        setSelectedPreset(value);
    };

    const handleApplyPreset = () => {
        if (!selectedMaterial || !selectedPreset) return;

        const preset = materialPresets.find((preset) => preset.id === selectedPreset);
        if (!preset) return;

        setModelMaterialPresets(prevModelPresetsMap => {
            const newMap = new Map(prevModelPresetsMap);
            newMap.set(selectedMaterial, preset);
            return newMap;
        })
    }

    const handleRemovePreset = () => {
        if (!selectedMaterial) return;

        setModelMaterialPresets(prevModelPresetsMap => {
            const newMap = new Map(prevModelPresetsMap);
            newMap.delete(selectedMaterial);
            return newMap;
        })
    }

    const handleSave = () => {
        updateModelMaterialPresets(variantId.current, modelMaterialPresets)
            .then(() => {
                modelMaterialPresets.size > 0 ?
                    alert(`Model updated with ${modelMaterialPresets.size} selected preset${modelMaterialPresets.size > 1 ? 's' : ''}`) :
                    alert('Model updated.');
            })
            .catch((error) => {
                console.error('Error during model presets updating:', error);
                alert('Error during model presets updating.');
            });
    }

    // Add this helper function to check if the selected material has a preset applied
    const hasPresetApplied = (materialName) => {
        const appliedPreset = modelMaterialPresets.get(materialName);
        return appliedPreset && selectedPreset && appliedPreset.id === selectedPreset;
    };

    // Update the button text and click handler based on whether a preset is applied
    const applyButtonText = selectedMaterial && selectedPreset && hasPresetApplied(selectedMaterial)
        ? "Remove"
        : "Apply";

    const handleApplyOrRemove = selectedMaterial && selectedPreset && hasPresetApplied(selectedMaterial)
        ? handleRemovePreset
        : handleApplyPreset;

    const handleCreatePreset = () => {
        let errorMessage = '';
        if (presetNameRef.current.input.value === undefined || presetNameRef.current.input.value === '') {
            errorMessage += 'Please enter a name for the new Preset.\n';
        }
        if (newMaterialNameRef.current.input.value === undefined || newMaterialNameRef.current.input.value === '') {
            errorMessage += 'Please enter a name for the new Material.\n';
        }
        if (errorMessage !== '') {
            alert(errorMessage);
            return;
        }

        const config = materialConfigsState[selectedMaterialName];
        if (!config) {
            alert('No configuration found for the selected material.');
            return;
        }

        const preset = {
            name: presetNameRef.current.input.value,
            newMaterialName: newMaterialNameRef.current.input.value,
            ...config
        };

        storeMaterialPreset(preset).then(() => {
            alert('Preset saved successfully.');
            // aggiorno la pagina
            window.location.reload();
        }).catch((error) => {
            console.error('Error during preset saving:', error);
            alert('Error during preset saving.');
        });
    };

    const getFileNameFromUrl = (url) => {
        if (!url) return 'model.glb';
        try {
            const pathname = url.split('?')[0];
            const decodedPath = decodeURIComponent(pathname);
            const parts = decodedPath.split('/');
            return parts[parts.length - 1] || 'model.glb';
        } catch {
            return 'model.glb';
        }
    }

    const handleSearch = (input, option) => {
        const label = option.label || option.children || '';
        return label.toLowerCase().includes(input.toLowerCase());
    };

    const handleGLBExport = () => {
        const scene = viewerRef.current.getScene();
        const model = scene.getObjectByName('Scene');
        if (!model) {
            alert("Model not found!");
            return;
        }
        const exporter = new GLTFExporter();

        const clonedMaterialsMap = new Map(); // mappa per contenere i materiali già clonati

        const modelHasAnimations = model.animations && model.animations.length > 0;

        // 1. Export di model
        // Clono il modello completo per applicare le modifiche ai materiali
        const modelClone = getClonedModel(model, modelHasAnimations, clonedMaterialsMap, false);
        // console.log('modelClone', modelClone);

        // Export di modelClone 
        exporter.parse(
            modelClone,
            (result) => {
                const blob = new Blob([result], { type: "application/octet-stream" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = getFileNameFromUrl(src);
                link.click();

                // Rimozione di eventuali preset salvati su firebase
                let newModelMaterialPresets = new Map();
                updateModelMaterialPresets(variantId.current, newModelMaterialPresets).catch((error) => {
                    console.error('Error during model presets deleting on Firebase:', error);
                    alert('Error during model presets deleting on Firebase.');
                });
                setModelMaterialPresets(newModelMaterialPresets);
            },
            (error) => {
                console.error('An error happened during parsing', error);
            },
            {
                binary: true,
                animations: modelClone.animations || []
            }
        );

        // 2. Export di modelCut
        if (!modelCut.current) {
            console.error("Model cut version not found!");
            alert("Model cut version not found!");
            return;
        }
        
        const modelCutHasAnimations = modelCut.current.animations && modelCut.current.animations.length > 0;
        // Clono modelCut e applico le modifiche ai materiali come in model, se possibile
        const modelCutClone = getClonedModel(modelCut.current, modelCutHasAnimations, clonedMaterialsMap, true);
        // console.log('modelCutClone', modelCutClone);
        // console.log('clonedMaterialsMap', clonedMaterialsMap);

        // Export di modelCutClone
        if (modelCutClone !== null) {
            exporter.parse(
                modelCutClone,
                (result) => {
                    const blob = new Blob([result], { type: "application/octet-stream" });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = getFileNameFromUrl(srcCutVers);
                    link.click();
                },
                (error) => {
                    console.log('An error happened during parsing', error);
                },
                {
                    binary: true,
                    animations: modelCutClone.animations || []
                }
            );
        }
    }

    /** 
     * Funzione per clonare il modello e i suoi materiali in profondità 
     * @param {THREE.Object3D} model - Il modello da clonare 
     * @returns {THREE.Object3D} - Il modello clonato 
     */
    const getClonedModel = (model, hasAnimations, clonedMaterialsMap, isCutVersion = false) => {
        let modelClone = null;
        if (hasAnimations) {
            if (typeof model.resetAllAnimations === "function") model.resetAllAnimations();

            modelClone = clone(model); // clone(model) funzione di SkeletonUtils che clona il modello animato e tutti i suoi figli
            
            if (typeof model.initAllAnimations === "function") model.initAllAnimations();
        } else {
            modelClone = model.clone(true); // deep clone, include children
        }

        if (typeof model.getOriginalMaterial === "function") {
            modelClone.getOriginalMaterial = model.getOriginalMaterial;
        }

        let errorBox = { value: '' };

        // Clono i materiali in profondità
        modelClone.traverse(child => {
            if (child.material) {
                // Se il materiale è un array (mesh con multi-materiale)
                if (Array.isArray(child.material)) {
                    child.material = child.material.map(mat => {
                        return getClonedMaterial(mat, clonedMaterialsMap, modelClone.getOriginalMaterial, isCutVersion, errorBox);
                    });
                } else {
                    const mat = child.material;
                    child.material = getClonedMaterial(mat, clonedMaterialsMap, modelClone.getOriginalMaterial, isCutVersion, errorBox);
                }
            }
        });

        if (errorBox.value.trim() !== '') {
            console.error('Error during model cut version export:', errorBox.value);
            alert('Error during model cut version export.\n' + errorBox.value);
            return null;
        }

        return modelClone;
    }

    const getClonedMaterial = (mat, clonedMaterialsMap, getOriginalMaterial, isCutVersion, errorBox) => {
        const matName = mat.name;
        if (!clonedMaterialsMap.has(matName)) {
            if (!isCutVersion) { // Modello completo, gestione materiali clonati se con preset o modificati live
                //  console.log(`material ${matName} not found in clonedMaterialsMap, editing and adding it...`);
                const matClone = mat.clone();
                if (modelMaterialPresets.has(matName)) {
                    // Update del nome dei materiali con preset applicati
                    const preset = modelMaterialPresets.get(matName);
                    if (preset.newMaterialName) {
                        matClone.name = preset.newMaterialName;
                    }
                    // Normalizzazione intensità colorSheen e sheen a 1 per glb
                    if (matClone.sheen > 0 && matClone.sheen < 1) {
                        let materialSheenColor = matClone.sheenColor;
                        matClone.sheenColor = materialSheenColor.clone().multiplyScalar(matClone.sheen);
                        // console.log('material.sheenColor after', material.sheenColor.getHexString());
                        matClone.sheen = 1;
                        // console.log('material.sheen after', material.sheen);
                    }
                } else if (typeof getOriginalMaterial === "function") {
                    // Ripristina il materiale originale.
                    const originalMat = getOriginalMaterial(matClone);
                    // console.log('originalMat', originalMat);
                    if (originalMat) {
                        matClone.copy(originalMat);
                        // console.log('matClone after copy', matClone);
                    }
                }
                // aggiunge il materiale clonato alla mappa
                clonedMaterialsMap.set(matName, matClone);
            } else { // caso modello versione cut, gestione vecchia nomenclatura e riutilizzo materiale clonato modello completo
                if (matName.endsWith('_VTO') && !errorBox.value.includes('_VTO')) {
                    errorBox.value += `Cannot export the model cut version: error in materials name!\nFound '_VTO' in their name\n`;
                }
                if (matName.endsWith('_MDV')) {
                    // cerco il material clonato con nome 3DV invece di MDV in clonedMaterialsMap
                    const name3dv = matName.replace('MDV', '3DV');
                    if (!clonedMaterialsMap.has(name3dv) && !errorBox.value.includes('_MDV')) {
                        errorBox.value += `Cannot export the model cut version: error in materials name!\nFound '_MDV' in their name\n`;
                    } else {
                        return clonedMaterialsMap.get(name3dv);
                    }
                }
                if (matName.endsWith('_3DV')) {
                    errorBox.value += `Cannot export the model cut version: error in materials name!\nNot found the material ${matName} in the complete model!\n`;
                }
                return mat.clone();
            }
        }
        return clonedMaterialsMap.get(matName);
    }

    return (
        <>
            {error && (
                <div style={{
                    backgroundColor: "#f8d7da",
                    color: "#721c24",
                    padding: "10px",
                    borderRadius: "5px",
                    margin: "10px 0",
                    textAlign: "center"
                }}>
                    {error}
                </div>
            )}

            <ModelContainerFrontEnd modelSelected={modelInView} project={PROJECT_PARAMS} singleView>
                {!navigator.userAgent.match(/FBAV/i) && (
                    <>
                        <ThreeJSViewer
                            ref={viewerRef}
                            singleView
                            singleSrc={src}
                            singleUsdzSrc={usdzSrc}
                            project={PROJECT_PARAMS}
                            viewerPage
                            paramsId={params.token}
                            isMaterialPresets
                            modelMaterialPresets={modelMaterialPresets}
                        />
                    </>
                )}
            </ModelContainerFrontEnd>

            <div style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                position: "absolute",
                zIndex: 10,
                top: "4%",
                left: "4%",
                width: 400
            }}>
                <section>
                    <h1>Material Presets</h1>

                    <h3>Create a Preset from a Material</h3>
                    <Col>
                        <Row style={{ marginTop: "10px" }}>
                            <Text>Select the Material</Text>
                            <Select
                                mode="single"
                                placeholder="Material"
                                value={selectedMaterialName}
                                onChange={handleMaterialNameChange}
                                style={{ width: "100%" }}
                                allowClear
                                showSearch
                                filterOption={(input, option) => handleSearch(input, option)}
                            >
                                {materialNamesList && materialNamesList.length > 0 ? materialNamesList.map((name, index) => (
                                    <Option key={index} value={name}>
                                        {name}
                                    </Option>
                                )) : <Option value="">No materials available</Option>}
                            </Select>
                        </Row>
                        <Row style={{ marginTop: "10px" }}>
                            <Text>Set the preset name</Text>
                            <Input
                                ref={presetNameRef}
                                placeholder="Preset Name"
                                style={{ width: "100%" }}
                                disabled={!selectedMaterialName}
                            />
                        </Row>
                        <Row style={{ marginTop: "10px" }}>
                            <Text>Set the new material name</Text>
                            <Input
                                ref={newMaterialNameRef}
                                placeholder={selectedMaterialName ?? "New Material Name"}
                                style={{ width: "100%" }}
                                disabled={!selectedMaterialName}
                            />
                        </Row>
                        <Row style={{ marginTop: "10px" }}>
                            <Button
                                type="primary"
                                onClick={handleCreatePreset}
                                disabled={!selectedMaterialName}
                            >
                                Create preset
                            </Button>
                        </Row>

                        <h3>Apply/Remove a Preset</h3>

                        <Row style={{ marginTop: "10px" }}>
                            <Text>Select the Material</Text>
                            <Select
                                mode="single"
                                placeholder="Material"
                                value={selectedMaterial}
                                onChange={handleMaterialChange}
                                style={{ width: "100%" }}
                                allowClear
                                showSearch
                                filterOption={(input, option) => handleSearch(input, option)}
                            >
                                {materialNamesList && materialNamesList.length > 0 ? materialNamesList.map((name, index) => (
                                    <Option key={index} value={name}>
                                        {name}
                                    </Option>
                                )) : <Option value="">No materials available</Option>}
                            </Select>
                        </Row>
                        <Row style={{ marginTop: "10px" }}>
                            <Text>Select a Preset</Text>
                            <Select
                                mode="single"
                                placeholder="Preset"
                                value={selectedPreset}
                                onChange={handlePresetChange}
                                style={{ width: "100%" }}
                                allowClear
                                disabled={!selectedMaterial}
                                showSearch
                                filterOption={(input, option) => handleSearch(input, option)}
                            >
                                {materialPresets.map((preset) => (
                                    <Option key={preset.id} value={preset.id}>
                                        {preset.name}
                                    </Option>
                                ))}
                            </Select>
                        </Row>
                        <Row style={{ marginTop: "10px" }}>
                            <Col>
                                <Button
                                    onClick={handleApplyOrRemove}
                                    disabled={!selectedMaterial || !selectedPreset}
                                >
                                    {applyButtonText}
                                </Button>
                            </Col>
                            <Col style={{ marginLeft: "6px" }}>
                                <Button
                                    type="primary"
                                    onClick={handleSave}
                                    disabled={!selectedMaterial || !selectedPreset}
                                >
                                    Save
                                </Button>
                            </Col>
                        </Row>

                        <h3>Export the updated Model</h3>

                        <Row style={{ marginTop: "10px" }}>
                            <Col>
                                <Button onClick={handleGLBExport}>
                                    Export GLB
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </section>
            </div>
        </>
    );
};

export default MaterialPresets;